import { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

const toKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Build a map: date string (YYYY-MM-DD) -> color from a booking
function buildDayMap(bookings) {
  const map = {};
  for (const b of bookings) {
    const start = new Date(b.start_date + "T00:00:00");
    const end = new Date(b.end_date + "T00:00:00");
    const cur = new Date(start);
    while (cur <= end) {
      map[toKey(cur)] = { color: b.color || "#1A2F24", name: b.member_name, soft: b.block_type === "soft" };
      cur.setDate(cur.getDate() + 1);
    }
  }
  return map;
}

export default function BookingCalendar({ bookings, onRangeSelect }) {
  const [range, setRange] = useState(undefined);
  const [months, setMonths] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768 ? 2 : 1
  );

  useEffect(() => {
    const handle = () => setMonths(window.innerWidth >= 768 ? 2 : 1);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  const dayMap = useMemo(() => buildDayMap(bookings), [bookings]);

  const bookedDates = useMemo(
    () => Object.keys(dayMap).map((k) => new Date(k + "T00:00:00")),
    [dayMap]
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleSelect = (r) => {
    setRange(r);
    if (r?.from && r?.to) {
      onRangeSelect(r);
      setTimeout(() => setRange(undefined), 200);
    }
  };

  const modifiers = { booked: bookedDates };

  const DayContent = ({ date }) => {
    const key = toKey(date);
    const info = dayMap[key];
    return (
      <span
        data-testid={`day-${key}`}
        data-block={info ? (info.soft ? "soft" : "hard") : undefined}
        title={info ? `${info.name} · ${info.soft ? "tentative" : "confirmed"}` : undefined}
        style={
          info
            ? {
                position: "relative",
                display: "inline-block",
                padding: "2px 2px 6px",
                color: "#1A2F24",
                fontWeight: 500,
              }
            : undefined
        }
      >
        {date.getDate()}
        {info && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 2,
              right: 2,
              bottom: 0,
              height: 3,
              borderRadius: 2,
              background: info.soft
                ? `repeating-linear-gradient(90deg, ${info.color} 0 3px, transparent 3px 6px)`
                : info.color,
            }}
          />
        )}
      </span>
    );
  };

  return (
    <div className="stj-calendar" data-testid="booking-calendar">
      <DayPicker
        mode="range"
        numberOfMonths={months}
        selected={range}
        onSelect={handleSelect}
        disabled={{ before: today }}
        modifiers={modifiers}
        modifiersClassNames={{ booked: "booked-day" }}
        components={{ DayContent }}
      />
    </div>
  );
}
